import React, { useState, useEffect } from 'react';
import { ShieldCheck, Plus, Pencil, X, Trash2, Scale, ListChecks } from 'lucide-react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { requestPin } from '../../components/PinModal';
import { toast } from 'sonner';

const TABS = [
  { id: 'config', label: 'Configuración General' },
  { id: 'categorias', label: 'Categorías' },
  { id: 'servicios', label: 'Servicios Digitales' },
  { id: 'historial', label: 'Consentimientos' },
  { id: 'legal', label: 'Integración Legal' },
];

const EMPTY_SERVICE = { name: '', category: 'terceros', description: '', provider: '', active: true, requiresConsent: true, domain: '', notes: '' };

export default function AdminConsentimientos() {
  const [tab, setTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [consents, setConsents] = useState([]);
  const [legalDocs, setLegalDocs] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [deletingService, setDeletingService] = useState(null);

  const load = () => {
    api.getConsentConfig().then((d) => {
      setConfig(d.config);
      setCategories(d.categories);
    }).catch(() => {});
    api.getConsentServices().then(setServices).catch(() => {});
    api.getConsents().then(setConsents).catch(() => {});
    api.getLegalActive().then(setLegalDocs).catch(() => {});
  };
  useEffect(load, []);

  const saveConfig = async () => {
    const token = await requestPin('Publicar configuración de consentimientos');
    if (!token) return;
    try {
      const { id, ...data } = config;
      await api.updateConsentConfig(data, token);
      toast.success('Configuración publicada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const saveService = async () => {
    if (!editingService.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    try {
      if (editingService.id) {
        const { id, ...data } = editingService;
        const updated = await api.updateConsentService(id, data);
        setServices((prev) => prev.map((s) => s.id === id ? updated : s));
      } else {
        const created = await api.createConsentService(editingService);
        setServices((prev) => [...prev, created]);
      }
      setEditingService(null);
      toast.success('Servicio guardado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const toggleService = async (s) => {
    try {
      const updated = await api.updateConsentService(s.id, { active: !s.active });
      setServices((prev) => prev.map((x) => x.id === s.id ? updated : x));
    } catch { toast.error('Error al actualizar'); }
  };

  const confirmDeleteService = async () => {
    const token = await requestPin(`Eliminar servicio "${deletingService.name}"`);
    if (!token) return;
    try {
      await api.deleteConsentService(deletingService.id, token);
      setServices((prev) => prev.filter((s) => s.id !== deletingService.id));
      setDeletingService(null);
      toast.success('Servicio eliminado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const setC = (k) => (e) => setConfig({ ...config, [k]: e.target.value });
  const catLabel = (id) => categories.find((c) => c.id === id)?.label || id;

  return (
    <div data-testid="admin-consentimientos">
      <div className="flex items-center gap-2 mb-1">
        <ShieldCheck className="w-6 h-6 text-amber-500" />
        <h1 className="text-2xl font-black text-stone-800">Consentimientos</h1>
      </div>
      <p className="text-sm text-stone-500 mb-6">Módulo SCME multiplataforma: una única administración para todo el ecosistema.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 text-sm font-bold rounded-full border transition-colors ${tab === t.id ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid={`consent-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* CONFIGURACIÓN GENERAL */}
      {tab === 'config' && config && (
        <div className="bg-white rounded-lg border border-stone-200 p-6 max-w-3xl space-y-4" data-testid="consent-config-form">
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Título del banner</label>
            <input value={config.bannerTitle} onChange={setC('bannerTitle')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="consent-banner-title" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Texto del banner</label>
            <textarea value={config.bannerText} onChange={setC('bannerText')} rows={3} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Mensaje informativo (panel de preferencias)</label>
            <textarea value={config.infoMessage} onChange={setC('infoMessage')} rows={8} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Botón aceptar</label>
              <input value={config.acceptAllLabel} onChange={setC('acceptAllLabel')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Botón rechazar</label>
              <input value={config.rejectLabel} onChange={setC('rejectLabel')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 mb-1">Botón preferencias</label>
              <input value={config.preferencesLabel} onChange={setC('preferencesLabel')} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
            </div>
          </div>
          <div className="border-t border-stone-100 pt-4 flex items-center gap-3">
            <button onClick={saveConfig} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="consent-config-save">Publicar cambios</button>
            <span className="text-xs text-stone-400">Cambio global: requiere PIN Maestro.</span>
          </div>
        </div>
      )}

      {/* CATEGORÍAS */}
      {tab === 'categorias' && (
        <div className="max-w-3xl">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4 flex items-center gap-3 text-sm text-stone-600">
            <ListChecks className="w-5 h-5 text-sky-500 shrink-0" />
            <span>Las categorías se gestionan desde <strong>Listados de Valores → Categorías de consentimiento</strong> (nada hardcodeado).</span>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Categoría</th>
                  <th className="px-4 py-3 font-medium">Descripción</th>
                  <th className="px-4 py-3 font-medium w-24">Servicios</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c.id} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-bold text-stone-800">{c.label}</td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{c.description}</td>
                    <td className="px-4 py-3 text-stone-600 text-center">{services.filter((s) => s.category === c.id).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SERVICIOS DIGITALES */}
      {tab === 'servicios' && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setEditingService({ ...EMPTY_SERVICE })} className="btn-natur flex items-center gap-2 px-4 py-2.5 rounded font-bold text-sm" data-testid="new-service-button">
              <Plus className="w-4 h-4" /> Nuevo servicio
            </button>
          </div>

          {deletingService && (
            <div className="bg-white rounded-lg border-2 border-red-300 p-6 mb-6" data-testid="service-delete-confirm">
              <h2 className="font-black text-stone-800 mb-2">Eliminar “{deletingService.name}”</h2>
              <div className="flex gap-3">
                <button onClick={confirmDeleteService} className="bg-red-600 hover:bg-red-700 transition-colors text-white px-5 py-2.5 rounded text-sm font-bold">Eliminar</button>
                <button onClick={() => setDeletingService(null)} className="px-4 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
              </div>
            </div>
          )}

          {editingService && (
            <div className="bg-white rounded-lg border-2 border-amber-300 p-6 mb-6" data-testid="service-form">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-black text-stone-800">{editingService.id ? `Editar ${editingService.name}` : 'Nuevo servicio digital'}</h2>
                <button onClick={() => setEditingService(null)} className="text-stone-400 hover:text-stone-700 transition-colors"><X className="w-5 h-5" /></button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 mb-1">Nombre *</label>
                  <input value={editingService.name} onChange={(e) => setEditingService({ ...editingService, name: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="service-name-input" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 mb-1">Categoría</label>
                  <select value={editingService.category} onChange={(e) => setEditingService({ ...editingService, category: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="service-category-select">
                    {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 mb-1">Proveedor</label>
                  <input value={editingService.provider} onChange={(e) => setEditingService({ ...editingService, provider: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-stone-500 mb-1">Descripción</label>
                  <input value={editingService.description} onChange={(e) => setEditingService({ ...editingService, description: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 mb-1">Dominio asociado</label>
                  <input value={editingService.domain} onChange={(e) => setEditingService({ ...editingService, domain: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-stone-500 mb-1">Observaciones</label>
                  <input value={editingService.notes} onChange={(e) => setEditingService({ ...editingService, notes: e.target.value })} className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
                </div>
                <div className="flex items-center gap-3 pt-4">
                  <Switch checked={editingService.requiresConsent} onCheckedChange={(v) => setEditingService({ ...editingService, requiresConsent: v })} data-testid="service-requires-consent" />
                  <span className="text-xs font-bold text-stone-600">Requiere consentimiento</span>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-5">
                <button onClick={() => setEditingService(null)} className="px-5 py-2.5 rounded text-sm font-bold text-stone-600 hover:bg-stone-100 transition-colors">Cancelar</button>
                <button onClick={saveService} className="btn-natur px-6 py-2.5 rounded text-sm font-bold" data-testid="save-service-button">Guardar servicio</button>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Servicio</th>
                  <th className="px-4 py-3 font-medium">Categoría</th>
                  <th className="px-4 py-3 font-medium">Proveedor</th>
                  <th className="px-4 py-3 font-medium">Consentimiento</th>
                  <th className="px-4 py-3 font-medium w-20">Activo</th>
                  <th className="px-4 py-3 w-20"></th>
                </tr>
              </thead>
              <tbody>
                {services.map((s) => (
                  <tr key={s.id} className="border-t border-stone-100" data-testid={`service-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <p className="font-bold text-stone-800">{s.name}</p>
                      <p className="text-xs text-stone-400">{s.domain}</p>
                    </td>
                    <td className="px-4 py-3"><span className="text-xs font-bold bg-stone-100 text-stone-600 px-2 py-1 rounded">{catLabel(s.category)}</span></td>
                    <td className="px-4 py-3 text-stone-600 text-xs">{s.provider}</td>
                    <td className="px-4 py-3 text-xs font-bold">{s.requiresConsent ? <span className="text-amber-600">Requerido</span> : <span className="text-teal-600">Exento</span>}</td>
                    <td className="px-4 py-3"><Switch checked={s.active} onCheckedChange={() => toggleService(s)} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => setEditingService({ ...s })} className="text-stone-400 hover:text-amber-600 transition-colors" data-testid={`edit-service-${s.id}`}><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeletingService(s)} className="text-stone-400 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* HISTORIAL DE CONSENTIMIENTOS */}
      {tab === 'historial' && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden" data-testid="consent-history">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-stone-500 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Fecha y hora</th>
                <th className="px-4 py-3 font-medium">Plataforma</th>
                <th className="px-4 py-3 font-medium">Preferencias</th>
                <th className="px-4 py-3 font-medium">Versión legal</th>
                <th className="px-4 py-3 font-medium">Estado</th>
              </tr>
            </thead>
            <tbody>
              {consents.map((c) => (
                <tr key={c.id} className="border-t border-stone-100 align-top" data-testid={`consent-row-${c.id}`}>
                  <td className="px-4 py-3 text-stone-600 text-xs">{new Date(c.createdAt).toLocaleString('es-ES')}</td>
                  <td className="px-4 py-3 text-stone-600 text-xs">{c.platform}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(c.preferences || {}).map(([k, v]) => (
                        <span key={k} className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${v ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-400 line-through'}`}>{catLabel(k)}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-stone-400 text-[10px]">{Object.values(c.legalSnapshot || {}).join(' ')}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${c.granted ? 'bg-teal-100 text-teal-700' : 'bg-stone-100 text-stone-500'}`}>{c.granted ? 'Otorgado' : 'Solo necesarias'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {consents.length === 0 && <p className="text-center text-stone-400 py-10">Sin registros de consentimiento todavía.</p>}
        </div>
      )}

      {/* INTEGRACIÓN LEGAL */}
      {tab === 'legal' && (
        <div className="max-w-3xl">
          <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-4 flex items-center gap-3 text-sm text-stone-600">
            <Scale className="w-5 h-5 text-sky-500 shrink-0" />
            <span>Cada consentimiento se vincula automáticamente a las <strong>versiones legales activas</strong> en el momento de la aceptación (snapshot legal, misma filosofía que los pedidos). Las publicaciones nuevas nunca alteran registros históricos.</span>
          </div>
          <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Documento legal</th>
                  <th className="px-4 py-3 font-medium">Versión activa</th>
                  <th className="px-4 py-3 font-medium">Publicada</th>
                </tr>
              </thead>
              <tbody>
                {legalDocs.map((d) => (
                  <tr key={d.slug} className="border-t border-stone-100">
                    <td className="px-4 py-3 font-bold text-stone-800">{d.name}</td>
                    <td className="px-4 py-3"><span className="text-xs font-bold bg-teal-100 text-teal-700 px-2 py-1 rounded">{d.version}</span></td>
                    <td className="px-4 py-3 text-stone-500 text-xs">{d.publishedAt ? new Date(d.publishedAt).toLocaleDateString('es-ES') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-stone-400 mt-3">Los textos se gestionan desde <strong>Área Legal</strong> (Repositorio Legal Maestro con versionado).</p>
        </div>
      )}
    </div>
  );
}
